import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { hqApi } from "@/hq/hqApi";
import { Card, EmptyState, Skeleton, Pagination, SearchInput } from "@/hq/HQPrimitives";

const STATUS_TONE = {
  completed: "#0FA958",
  partial:   "#E8A03B",
  failed:    "#C1350B",
  running:   "#2E7CF6",
};

function ScoreBadge({ score }) {
  if (score == null) return <span className="text-[#8A8A8A]">—</span>;
  const tone = score >= 80 ? "#0FA958" : score >= 60 ? "#E8A03B" : "#C1350B";
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-black tracking-[0.14em]"
          style={{ background: `${tone}22`, color: tone, border: `1px solid ${tone}44` }}>
      {score}<span className="text-[9px] opacity-70">/100</span>
    </span>
  );
}

export default function HQOptimiseScans() {
  const [q, setQ] = useState("");
  const [band, setBand] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    hqApi.get("/optimise/summary")
      .then((r) => alive && setSummary(r.data))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    setData(null);
    const params = { page, page_size: 25 };
    if (q) params.q = q;
    if (band) params.band = band;
    hqApi.get("/optimise/scans", { params })
      .then((r) => alive && setData(r.data))
      .catch(() => alive && setErr("Something went wrong loading scans."));
    return () => { alive = false; };
  }, [q, band, page]);

  const items = data?.items || [];

  return (
    <div className="space-y-4" data-testid="hq-optimise-scans">
      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          ["total_scans", "Total scans"],
          ["average_orange_score", "Average Orange Score"],
          ["poor_score_websites", "Poor-score sites"],
          ["monitored_websites", "Monitored websites"],
        ].map(([key, label]) => (
          <Card key={key} className="p-5" data-testid={`hq-optimise-summary-${key}`}>
            <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#FF6A00]">{label}</div>
            <div className="mt-3 font-black text-3xl tracking-[-0.02em]">
              {summary ? (summary[key] ?? 0) : <Skeleton h={28} w={60} />}
            </div>
          </Card>
        ))}
      </div>

      <Card className="p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput value={q} onChange={(v) => { setQ(v); setPage(1); }} placeholder="Search domain…" testId="hq-optimise-scans-search" />
          <div className="inline-flex rounded-full bg-[#F6F5F2] p-1" data-testid="hq-optimise-band-filter">
            {[["", "All"], ["good", "Good"], ["needs_attention", "Needs"], ["poor", "Poor"]].map(([val, label]) => (
              <button
                key={val || "all"}
                type="button"
                onClick={() => { setBand(val); setPage(1); }}
                data-testid={`hq-optimise-band-${val || "all"}`}
                className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-[0.18em] uppercase transition-colors ${
                  band === val ? "bg-black text-white" : "text-black/60 hover:text-black"
                }`}
              >{label}</button>
            ))}
          </div>
          <div className="ml-auto text-[11px] tracking-[0.24em] uppercase text-[#8A8A8A]" data-testid="hq-optimise-scans-total">
            {data ? `${data.total} scan${data.total === 1 ? "" : "s"}` : "…"}
          </div>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        {err ? (
          <div className="p-6 text-sm text-[#C1350B]">{err}</div>
        ) : !data ? (
          <div className="p-6 space-y-3">{[0,1,2].map(i => <Skeleton key={i} h={40} />)}</div>
        ) : items.length === 0 ? (
          <EmptyState title="No scans yet" hint="Public scans and monitoring runs will appear here." testId="hq-optimise-scans-empty" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[14px]">
              <thead className="bg-[#F6F5F2] text-[10px] tracking-[0.22em] uppercase text-[#8A8A8A]">
                <tr>
                  <th className="text-left px-5 py-3 font-bold">Domain</th>
                  <th className="text-left px-5 py-3 font-bold">Score</th>
                  <th className="text-left px-5 py-3 font-bold">Complexity</th>
                  <th className="text-left px-5 py-3 font-bold">Status</th>
                  <th className="text-left px-5 py-3 font-bold">Origin</th>
                  <th className="text-left px-5 py-3 font-bold">When</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr key={s.id} className="border-t border-[#EAEAEA] hover:bg-[#FFFDF6]" data-testid={`hq-optimise-scan-row-${s.id}`}>
                    <td className="px-5 py-3 font-bold break-all">{s.normalized_domain || "—"}</td>
                    <td className="px-5 py-3"><ScoreBadge score={s.orange_score} /></td>
                    <td className="px-5 py-3 text-[13px] text-[#4A4A4A]">{s.complexity || "—"}</td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-black tracking-[0.16em] uppercase"
                            style={{ color: STATUS_TONE[s.status] || "#4A4A4A" }}>
                        {s.status || "—"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-[11px] tracking-[0.16em] uppercase text-[#8A8A8A]">{s.origin || "public"}</td>
                    <td className="px-5 py-3 text-[12px] text-[#8A8A8A]">
                      {s.completed_at ? new Date(s.completed_at).toLocaleString() : (s.started_at ? new Date(s.started_at).toLocaleString() : "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {data && <Pagination page={data.page} total_pages={data.total_pages} onPage={setPage} testIdPrefix="hq-optimise-scans-page" />}
    </div>
  );
}
